import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Users as UsersIcon, ShieldCheck } from "lucide-react";

function buildTree(users) {
  const byId = {};
  users.forEach((u) => { byId[u.id] = { ...u, children: [] }; });
  const roots = [];
  users.forEach((u) => {
    const node = byId[u.id];
    if (u.supervisor_id && byId[u.supervisor_id]) byId[u.supervisor_id].children.push(node);
    else roots.push(node);
  });
  return roots;
}

// Flatten the tree into a list of rows with depth info. Iterative traversal
// avoids the recursive JSX pattern that trips older babel plugins.
function flatten(roots, collapsed) {
  const out = [];
  const stack = roots.map((n) => ({ node: n, depth: 0 }));
  stack.reverse();
  while (stack.length) {
    const { node, depth } = stack.pop();
    out.push({ node, depth });
    if (!collapsed.has(node.id)) {
      const kids = [...node.children].reverse();
      kids.forEach((c) => stack.push({ node: c, depth: depth + 1 }));
    }
  }
  return out;
}

export function HierarchyTree() {
  const [users, setUsers] = useState([]);
  const [collapsed, setCollapsed] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.get("/users/hierarchy")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const toggle = (id) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsed(next);
  };

  const tree = buildTree(users);
  const rows = flatten(tree, collapsed);
  const orphans = users.filter((u) => u.supervisor_id && !users.find((x) => x.id === u.supervisor_id));

  return (
    <div className="border bg-card p-5 mb-4" data-testid="hierarchy-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Organigrama · Cadena de Aprobación</div>
          <p className="text-[10px] text-muted-foreground">Cada usuario aparece bajo su supervisor. Los montos que excedan un límite se escalan automáticamente hacia arriba.</p>
        </div>
        <button onClick={reload} className="text-[10px] font-bold uppercase text-primary hover:underline" data-testid="org-refresh">
          Actualizar
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground py-4 text-center">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center">Sin usuarios registrados</div>
      ) : (
        <div className="text-xs border-t" data-testid="org-tree-root">
          {rows.map(({ node, depth }) => {
            const hasChildren = node.children.length > 0;
            const isCollapsed = collapsed.has(node.id);
            return (
              <div key={node.id}
                   className="flex items-center gap-2 py-1.5 pl-2 pr-3 border-b hover:bg-muted"
                   style={{ paddingLeft: `${8 + depth * 22}px` }}
                   data-testid={`org-node-${node.id}`}>
                <button
                  type="button"
                  onClick={() => hasChildren && toggle(node.id)}
                  className={`w-4 h-4 flex items-center justify-center ${hasChildren ? "" : "invisible"}`}
                  data-testid={`org-toggle-${node.id}`}
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                </button>
                {node.role === "admin"
                  ? <ShieldCheck size={14} className="text-primary" />
                  : <UsersIcon size={14} className="text-muted-foreground" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-xs">{node.name || node.email}</span>
                    <span className="text-[9px] font-bold uppercase text-muted-foreground tracking-widest">{node.role}</span>
                    {node.active === false && (
                      <span className="text-[9px] font-bold uppercase text-destructive">inactivo</span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>{node.email}</span>
                    <span className="tabular-nums">
                      Límite: {node.role === "admin"
                        ? "sin tope"
                        : `${Number(node.approval_limit || 0).toLocaleString()} ${node.approval_currency || "MXN"}`}
                    </span>
                    {hasChildren && <span>· {node.children.length} directo(s)</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {orphans.length > 0 && (
        <div className="mt-3 text-[10px] text-destructive">
          {orphans.length} usuario(s) tienen supervisor asignado que fue eliminado. Ajusta desde el editor de Permisos.
        </div>
      )}
    </div>
  );
}
